import { useState, useEffect, useRef } from "react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdRefresh } from "react-icons/md";
import { useAppSelector } from "../../store/hooks";
import { selectTradingProviders } from "../../store/settingsSlice";
import { ProviderFactory } from "../../services/providers";
import type { TradingProviderType } from "../../services/providers";

// Mock types for demonstration
type OrderStatus = 'OPEN' | 'COMPLETE' | 'REJECTED' | 'CANCELLED';
type TransactionType = 'BUY' | 'SELL';

interface OrderResponse {
    order_id: string;
    order_timestamp: string;
    exchange_update_timestamp?: string;
    transaction_type: TransactionType;
    order_type: string;
    tradingSymbol: string;
    exchange: string;
    filled_quantity: number;
    quantity: number;
    price: number;
    trigger_price: number;
    average_price: number;
    status: OrderStatus;
    product: string;
    validity: string;
    status_message?: string;
    providerName: string; // Track which provider this order came from
    connectionId: string; // Connection ID for operations
}

type OrdersPageState = {
    isLoading: boolean;
    message: string;
}

type SortBy = 'time' | 'provider' | 'symbol';

const Orders = () => {
    const [ordersPageState, setOrdersPageState] = useState<OrdersPageState>({
        isLoading: false,
        message: ""
    });

    const [allOrders, setAllOrders] = useState<OrderResponse[]>([]);
    const [sortBy, setSortBy] = useState<SortBy>('time');
    const tradingProviders = useAppSelector(selectTradingProviders);

    const fetchOrders = async () => {
        setOrdersPageState({ isLoading: true, message: "" });

        try {
            // Find all connected trading providers
            const connectedProviders = tradingProviders.filter(p => p.status === 'connected');

            if (connectedProviders.length === 0) {
                setOrdersPageState({
                    isLoading: false,
                    message: "No trading provider connected. Please connect a provider in Account settings."
                });
                setAllOrders([]);
                return;
            }

            // Fetch orders from all connected providers
            const allOrdersPromises = connectedProviders.map(async (provider) => {
                try {
                    const providerInstance = ProviderFactory.getTradingProvider(
                        provider.providerName as TradingProviderType
                    );

                    if (!providerInstance) {
                        console.warn(`Failed to initialize ${provider.providerName} provider`);
                        return [];
                    }

                    // Update provider with connection details
                    providerInstance.updateConnectionDetails(provider);

                    // Fetch orders from this provider
                    const orders = await providerInstance.getOrders();

                    // Map and add provider info to each order
                    return orders.map((order: any) => ({
                        order_id: order.order_id || order.orderId,
                        order_timestamp: order.order_timestamp || order.orderTimestamp,
                        exchange_update_timestamp: order.exchange_update_timestamp || order.exchangeUpdateTimestamp,
                        transaction_type: order.transaction_type || order.transactionType,
                        order_type: order.order_type || order.orderType,
                        tradingSymbol: order.tradingSymbol || order.trading_symbol,
                        exchange: order.exchange,
                        filled_quantity: order.filled_quantity || order.filledQuantity || 0,
                        quantity: order.quantity,
                        price: order.price,
                        trigger_price: order.trigger_price || order.triggerPrice || 0,
                        average_price: order.average_price || order.averagePrice || 0,
                        status: order.status,
                        product: order.product,
                        validity: order.validity,
                        status_message: order.status_message || order.statusMessage,
                        providerName: provider.providerName,
                        connectionId: provider.id
                    }));
                } catch (error) {
                    console.error(`Error fetching orders from ${provider.providerName}:`, error);
                    return [];
                }
            });

            const ordersArrays = await Promise.all(allOrdersPromises);
            const allFetchedOrders = ordersArrays.flat();

            setOrdersPageState({ isLoading: false, message: "" });
            setAllOrders(allFetchedOrders);
        } catch (error) {
            console.error('[Orders] Error fetching orders:', error);
            setOrdersPageState({
                isLoading: false,
                message: `Failed to fetch orders: ${error instanceof Error ? error.message : 'Unknown error'}`
            });
            setAllOrders([]);
        }
    };

    const reloadOrders = async () => {
        await fetchOrders();
    };

    useEffect(() => {
        fetchOrders();
    }, [tradingProviders]);

    // Sort orders based on selected sorting method
    const sortedOrders = [...allOrders].sort((a, b) => {
        // Always prioritize OPEN orders first
        if (a.status === 'OPEN' && b.status !== 'OPEN') return -1;
        if (a.status !== 'OPEN' && b.status === 'OPEN') return 1;

        // Then apply selected sorting
        switch (sortBy) {
            case 'provider':
                const providerCompare = a.providerName.localeCompare(b.providerName);
                if (providerCompare !== 0) return providerCompare;
                // If same provider, sort by time
                return new Date(b.order_timestamp).getTime() - new Date(a.order_timestamp).getTime();

            case 'symbol':
                const symbolCompare = a.tradingSymbol.localeCompare(b.tradingSymbol);
                if (symbolCompare !== 0) return symbolCompare;
                // If same symbol, sort by time
                return new Date(b.order_timestamp).getTime() - new Date(a.order_timestamp).getTime();

            case 'time':
            default:
                return new Date(b.order_timestamp).getTime() - new Date(a.order_timestamp).getTime();
        }
    });

    return (
        <div
            className="flex flex-col h-full"
            style={{
                padding: 'var(--spacing-md)',
                // backgroundColor: 'var(--color-background-secondary)'
            }}
        >
            {ordersPageState.isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <div
                        className="w-12 h-12 rounded-full animate-spin"
                        style={{
                            border: '3px solid var(--color-gray-200)',
                            borderTopColor: 'var(--color-primary-600)'
                        }}
                    ></div>
                </div>
            ) : (
                <>
                    {/* Show error/info message if exists */}
                    {ordersPageState.message && (
                        <div
                            style={{
                                padding: 'var(--spacing-md)',
                                backgroundColor: 'var(--color-warning-50)',
                                border: '1px solid var(--color-warning-200)',
                                borderRadius: 'var(--radius-md)',
                                color: 'var(--color-warning-700)',
                                fontSize: 'var(--font-size-sm)',
                                marginBottom: 'var(--spacing-md)'
                            }}
                        >
                            {ordersPageState.message}
                        </div>
                    )}

                    <div
                        style={{
                            // backgroundColor: 'var(--color-surface)',
                            borderRadius: 'var(--radius-md)',
                            boxShadow: 'var(--shadow-sm)'
                        }}
                    >
                        <OrdersTable
                            orders={sortedOrders}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            onRefresh={reloadOrders}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

interface OrdersTableProps {
    orders: OrderResponse[];
    sortBy: SortBy;
    setSortBy: (sortBy: SortBy) => void;
    onRefresh: () => void;
}

const OrdersTable = ({ orders, sortBy, setSortBy, onRefresh }: OrdersTableProps) => {
    const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const tradingProviders = useAppSelector(selectTradingProviders);

    const toggleDropdown = (index: number) => {
        setOpenDropdownIndex(prev => (prev === index ? null : index));
    };

    const handleModifyOrder = (order: OrderResponse) => {
        console.log("Modify order:", order);
        setOpenDropdownIndex(null);
    };

    const handleHeaderClick = (column: SortBy) => {
        setSortBy(column);
    };

    const getSortIcon = (column: SortBy) => {
        if (sortBy !== column) return null;
        return (
            <span style={{ marginLeft: 'var(--spacing-xs)' }}>▼</span>
        );
    };

    // Get provider display info
    const getProviderInfo = (providerName: string) => {
        switch (providerName.toLowerCase()) {
            case 'kite':
                return { name: 'Kite', color: 'var(--color-primary-600)', bgColor: 'var(--color-primary-50)' };
            case 'papertrade':
                return { name: 'Paper', color: 'var(--color-warning-600)', bgColor: 'var(--color-warning-50)' };
            default:
                return { name: providerName, color: 'var(--color-gray-600)', bgColor: 'var(--color-gray-50)' };
        }
    };

    const handleCancelOrder = async (order: OrderResponse) => {
        try {
            // Find the provider for this order
            const provider = tradingProviders.find(p => p.id === order.connectionId);

            if (!provider) {
                alert('Provider connection not found.');
                return;
            }

            // Get the provider instance
            const providerInstance = ProviderFactory.getTradingProvider(
                provider.providerName as TradingProviderType
            );

            if (!providerInstance) {
                alert('Failed to initialize trading provider.');
                return;
            }

            // Update provider with connection details
            providerInstance.updateConnectionDetails(provider);

            // Cancel the order
            await providerInstance.cancelOrder(order.order_id);

            alert(`Order ${order.order_id} cancelled successfully`);

            // Reload orders to reflect the change
            window.location.reload(); // Simple approach - you can improve this with state management
        } catch (error) {
            console.error('[Orders] Error cancelling order:', error); ``
            alert(`Failed to cancel order: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setOpenDropdownIndex(null);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.relative') || !target.closest('svg')) {
                setOpenDropdownIndex(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getOrderOptions = (order: OrderResponse) => {
        switch (order.status) {
            case 'OPEN':
                return (
                    <div
                        className="absolute top-full right-0 flex flex-col overflow-hidden"
                        style={{
                            marginTop: 'var(--spacing-xs)',
                            backgroundColor: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)',
                            boxShadow: 'var(--shadow-lg)',
                            border: '1px solid var(--color-border-light)',
                            zIndex: 'var(--z-index-dropdown)',
                            minWidth: '10rem'
                        }}
                    >
                        <div
                            className="cursor-pointer whitespace-nowrap"
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                color: 'var(--color-text-primary)',
                                transition: 'background-color var(--transition-fast)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-50)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => handleModifyOrder(order)}
                        >
                            Modify Order
                        </div>
                        <div
                            className="cursor-pointer whitespace-nowrap"
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                color: 'var(--color-danger-600)',
                                transition: 'background-color var(--transition-fast)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-danger-50)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => handleCancelOrder(order)}
                        >
                            Cancel Order
                        </div>
                    </div>
                );
            default:
                return (
                    <div
                        className="absolute top-full right-0 flex flex-col overflow-hidden"
                        style={{
                            marginTop: 'var(--spacing-xs)',
                            backgroundColor: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)',
                            boxShadow: 'var(--shadow-lg)',
                            border: '1px solid var(--color-border-light)',
                            zIndex: 'var(--z-index-dropdown)',
                            minWidth: '10rem'
                        }}
                    >
                        <div
                            className="cursor-pointer whitespace-nowrap"
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-medium)',
                                color: 'var(--color-text-primary)',
                                transition: 'background-color var(--transition-fast)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => console.log("View details for order:", order)}
                        >
                            View Details
                        </div>
                    </div>
                );
        }
    };

    const formatTime = (timestamp: string) => {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const formatDate = (timestamp: string) => {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <>
            <div className="overflow-x-auto">
                <table
                    className="w-full border-collapse"
                    style={{ fontSize: 'var(--font-size-sm)' }}
                >
                    <thead>
                        <tr
                            style={{
                                backgroundColor: 'var(--color-gray-50)',
                                borderBottom: '2px solid var(--color-border-light)'
                            }}
                        >
                            <th
                                className="text-left uppercase tracking-wider cursor-pointer"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: sortBy === 'time' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                                    userSelect: 'none',
                                    transition: 'color var(--transition-fast)'
                                }}
                                onClick={() => handleHeaderClick('time')}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary-600)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = sortBy === 'time' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)'}
                            >
                                Time{getSortIcon('time')}
                            </th>
                            <th
                                className="text-left uppercase tracking-wider cursor-pointer"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: sortBy === 'provider' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                                    userSelect: 'none',
                                    transition: 'color var(--transition-fast)'
                                }}
                                onClick={() => handleHeaderClick('provider')}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary-600)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = sortBy === 'provider' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)'}
                            >
                                Provider{getSortIcon('provider')}
                            </th>
                            <th
                                className="text-left uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >Type</th>
                            <th
                                className="text-left uppercase tracking-wider cursor-pointer"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: sortBy === 'symbol' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                                    userSelect: 'none',
                                    transition: 'color var(--transition-fast)'
                                }}
                                onClick={() => handleHeaderClick('symbol')}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary-600)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = sortBy === 'symbol' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)'}
                            >
                                Instrument{getSortIcon('symbol')}
                            </th>
                            <th
                                className="text-center uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >Qty</th>
                            <th
                                className="text-right uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >Price</th>
                            <th
                                className="text-right uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >Avg Price</th>
                            <th
                                className="text-center uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >Status</th>
                            <th
                                className="text-center uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)',
                                    position: 'relative'
                                }}
                            >
                                <MdRefresh
                                    size={16}
                                    className="cursor-pointer"
                                    style={{
                                        color: 'var(--color-text-tertiary)',
                                        transition: 'color var(--transition-fast), transform var(--transition-base)',
                                        display: 'inline-block'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = 'var(--color-primary-600)';
                                        e.currentTarget.style.transform = 'rotate(180deg)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'var(--color-text-tertiary)';
                                        e.currentTarget.style.transform = 'rotate(0deg)';
                                    }}
                                    onClick={onRefresh}
                                    title="Refresh orders"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        style={{
                            backgroundColor: 'var(--color-surface)',
                            borderTop: '1px solid var(--color-border-light)'
                        }}
                    >
                        {orders.length > 0 ? (
                            orders.map((order, index) => (
                                <tr
                                    key={index}
                                    style={{
                                        borderBottom: '1px solid var(--color-border-light)',
                                        transition: 'background-color var(--transition-fast)'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
                                >
                                    <td
                                        className="whitespace-nowrap"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <div className="flex flex-col" style={{ gap: 'var(--spacing-xs)' }}>
                                            <span
                                                style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 'var(--font-weight-medium)',
                                                    color: 'var(--color-text-primary)'
                                                }}
                                            >{formatTime(order.order_timestamp)}</span>
                                            <span
                                                style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)'
                                                }}
                                            >{formatDate(order.order_timestamp)}</span>
                                        </div>
                                    </td>
                                    <td
                                        className="whitespace-nowrap"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            className="inline-flex items-center justify-center uppercase w-fit"
                                            style={{
                                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                                borderRadius: 'var(--radius-sm)',
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 'var(--font-weight-bold)',
                                                backgroundColor: getProviderInfo(order.providerName).bgColor,
                                                color: getProviderInfo(order.providerName).color,
                                                border: `1px solid ${getProviderInfo(order.providerName).color}33`
                                            }}
                                        >
                                            {getProviderInfo(order.providerName).name}
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <div className="flex flex-col" style={{ gap: 'var(--spacing-xs)' }}>
                                            <span
                                                className="inline-flex items-center justify-center uppercase w-fit"
                                                style={{
                                                    padding: 'var(--spacing-xs) var(--spacing-sm)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    fontWeight: 'var(--font-weight-bold)',
                                                    backgroundColor: order.transaction_type.toLowerCase() === 'buy' ? 'var(--color-success-100)' : 'var(--color-danger-100)',
                                                    color: order.transaction_type.toLowerCase() === 'buy' ? 'var(--color-success-700)' : 'var(--color-danger-700)',
                                                    border: `1px solid ${order.transaction_type.toLowerCase() === 'buy' ? 'var(--color-success-200)' : 'var(--color-danger-200)'}`
                                                }}
                                            >
                                                {order.transaction_type}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)',
                                                    fontWeight: 'var(--font-weight-medium)'
                                                }}
                                            >{order.order_type}</span>
                                        </div>
                                    </td>
                                    <td
                                        className="whitespace-nowrap"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <div className="flex flex-col" style={{ gap: 'var(--spacing-xs)' }}>
                                            <span
                                                style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 'var(--font-weight-semibold)',
                                                    color: 'var(--color-text-primary)'
                                                }}
                                            >{order.tradingSymbol}</span>
                                            <span
                                                style={{
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-text-tertiary)'
                                                }}
                                            >{order.exchange}</span>
                                        </div>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-center"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        >
                                            {order.filled_quantity}/{order.quantity}
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-right"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        >
                                            {order.order_type === "SL-M" || order.order_type === "SL"
                                                ? `₹${order.trigger_price.toFixed(2)}`
                                                : `₹${order.price.toFixed(2)}`
                                            }
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-right"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        >₹{order.average_price.toFixed(2)}</span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-center"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            className="inline-flex items-center uppercase cursor-pointer"
                                            style={{
                                                padding: 'var(--spacing-sm) var(--spacing-xs)',
                                                borderRadius: 'var(--radius-md)',
                                                fontSize: 'var(--font-size-xs)',
                                                fontWeight: 'var(--font-weight-semibold)',
                                                backgroundColor:
                                                    order.status.toLowerCase() === 'complete' ? 'var(--color-success-100)' :
                                                        order.status.toLowerCase() === 'open' ? 'var(--color-warning-100)' :
                                                            order.status.toLowerCase() === 'rejected' ? 'var(--color-danger-100)' :
                                                                order.status.toLowerCase() === 'cancelled' ? 'var(--color-gray-200)' : 'var(--color-gray-100)',
                                                color:
                                                    order.status.toLowerCase() === 'complete' ? 'var(--color-success-800)' :
                                                        order.status.toLowerCase() === 'open' ? 'var(--color-warning-800)' :
                                                            order.status.toLowerCase() === 'rejected' ? 'var(--color-danger-800)' :
                                                                order.status.toLowerCase() === 'cancelled' ? 'var(--color-gray-700)' : 'var(--color-gray-600)',
                                                border: `2px solid ${order.status.toLowerCase() === 'complete' ? 'var(--color-success-300)' :
                                                    order.status.toLowerCase() === 'open' ? 'var(--color-warning-300)' :
                                                        order.status.toLowerCase() === 'rejected' ? 'var(--color-danger-300)' :
                                                            'var(--color-gray-300)'
                                                    }`,
                                                transition: 'box-shadow var(--transition-fast)'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                                            onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                                            onClick={() => console.log("View details for order:", order)}
                                        >
                                            {order.status}
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-center"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <div className="relative" ref={dropdownRef}>
                                            <HiOutlineDotsVertical
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleDropdown(index);
                                                }}
                                                className="cursor-pointer mx-auto"
                                                style={{
                                                    color: 'var(--color-text-tertiary)',
                                                    width: '1.25rem',
                                                    height: '1.25rem',
                                                    transition: 'color var(--transition-fast)'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-tertiary)'}
                                            />
                                            {openDropdownIndex === index && getOrderOptions(order)}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="text-center"
                                    style={{
                                        padding: 'var(--spacing-xl)',
                                        color: 'var(--color-text-tertiary)',
                                        fontSize: 'var(--font-size-sm)'
                                    }}
                                >
                                    No orders found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
}



export default Orders;
